/*
  The live channel (BUILD-PLAN 2.10, 2.11.4; architecture 12.1).

  One socket per browser tab. This class holds the rules and no React:

  - Notices are applied in number order. A notice that is already applied is dropped. A notice that
    skips ahead is kept back and the client asks for "events after N" over REST; the answer and the
    kept notices are applied together, in order.
  - A personal notice reaches only one person, so numbers a person sees may skip. The REST answer
    settles it: whatever it holds is what this person can see, and its `head` is where they stand.
  - After a reconnect the client catches up the same way. If the server says "resync required"
    (or the socket was closed with 4409), every query is fetched again.
  - The head the server announces every `heartbeat_s` shows a client that missed the last notice.
  - Heartbeats go out every `heartbeat_s` seconds with the screen, item and session, and feed presence.
  - Invalidations are gathered for a short moment and sent together, so a burst of events (twelve
    cards proposed at once) refetches each screen once.

  What happens to the data (the stores, the query cache, the toasts) goes through a `LiveSink`.
*/

import type { EventsAfter, EventNotice, FilesStatus, Id, PresenceEntry, ServerMessage, WsAgentBlock, WsAgentDelta, WsAgentTool, WsAgentTurnEnded, WsAgentTurnStarted, WsCounts, WsError, WsHello, WsLock, WsNotice, WsPointTo } from "../api/types";
import { WS_CLOSE_ACCOUNT_DISABLED, WS_CLOSE_MEMBERSHIP_CHANGED, WS_CLOSE_NOT_SIGNED_IN, WS_CLOSE_RESYNC, WS_PATH } from "../api/types";
import { isRefusal } from "../api/errors";
import type { QueryName } from "../api/routes";
import { NOTICE_INVALIDATIONS, invalidationsFor } from "./invalidations";
import type { LiveStatus } from "./store";

export type AgentWsMessage = WsAgentTurnStarted | WsAgentDelta | WsAgentBlock | WsAgentTool | WsAgentTurnEnded;

/** Where the client puts what it learns. The app wires it to the stores and the query cache. */
export interface LiveSink {
  status(status: LiveStatus): void;
  hello(message: WsHello): void;
  /** A notice, applied in order. Also called for notices found by catching up. */
  notice(notice: EventNotice): void;
  /** The number of the last notice applied. */
  applied(head: number): void;
  serverHead(head: number): void;
  presence(entries: PresenceEntry[]): void;
  lock(message: WsLock): void;
  files(status: FilesStatus): void;
  counts(message: WsCounts): void;
  agent(message: AgentWsMessage): void;
  personalNotice(message: WsNotice): void;
  pointTo(message: WsPointTo): void;
  error(message: WsError): void;
  /** Mark these endpoints stale; screens that show them fetch again. */
  invalidate(names: readonly QueryName[]): void;
  /** Fetch every query again. */
  refetchAll(): void;
  /** The first time the socket joined: queries older than `since` (milliseconds) may have missed events. */
  firstJoin(since: number): void;
  authLost(why: "not_signed_in" | "account_disabled"): void;
  membershipChanged(): void;
  /** The socket keeps failing: ask the server who the person is. */
  checkSession(): void;
}

/** The part of a WebSocket the client uses. */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event?: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event?: unknown) => void) | null;
}

export interface HeartbeatLocation {
  screen: string;
  item_id?: Id | null;
  session_id?: Id | null;
}

export interface LiveClientOptions {
  sink: LiveSink;
  /** `GET /api/ws/events?after=N&limit=...` */
  fetchEventsAfter(after: number, limit: number): Promise<EventsAfter>;
  /** The person's membership id: a personal notice for someone else is not shown. */
  membershipId?: Id | null;
  getLocation?: () => HeartbeatLocation;
  url?: string;
  createSocket?: (url: string) => SocketLike;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  random?: () => number;
  /** How long invalidations are gathered before they go out. */
  coalesceMs?: number;
  /** How long a "head is ahead of me" is allowed to settle before the client asks for events. */
  headSettleMs?: number;
  /** After this many failed attempts in a row the client asks the server whether the person is still signed in. */
  checkSessionAfter?: number;
  now?: () => number;
}

const OPEN = 1;
/** The size of one answer of `events_after`. */
export const CATCH_UP_PAGE = 200;
const CATCH_UP_MAX_PAGES = 5;
const CATCH_UP_RETRY_MS = 3000;
const KNOWN_TYPES = new Set([
  "hello",
  "event",
  "head",
  "resync",
  "presence",
  "lock",
  "files",
  "counts",
  "agent.turn_started",
  "agent.delta",
  "agent.block",
  "agent.tool",
  "agent.turn_ended",
  "notice",
  "point_to",
  "error",
]);

/** The address of the live channel, on the page's own host. */
export function buildWsUrl(location: Pick<Location, "protocol" | "host"> = window.location): string {
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${WS_PATH}`;
}

export function parseServerMessage(data: unknown): ServerMessage | null {
  if (typeof data !== "string") return null;
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== "string" || !KNOWN_TYPES.has(type)) return null;
  return value as ServerMessage;
}

export class LiveClient {
  private readonly options: LiveClientOptions;
  private socket: SocketLike | null = null;
  private stopped = true;
  private applied: number | null = null;
  private buffer = new Map<number, EventNotice>();
  private catchingUp = false;
  private catchUpAgain = false;
  private needsResync = false;
  private joinedOnce = false;
  private attempt = 0;
  private failedInARow = 0;
  private connectStartedAt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private headTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private invalidateTimer: ReturnType<typeof setTimeout> | null = null;
  private pending = new Set<QueryName>();

  constructor(options: LiveClientOptions) {
    this.options = options;
  }

  /** Open the socket. Calling it again while running does nothing. */
  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.connect();
  }

  /** Close the socket for good and drop what is waiting. */
  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.pending.clear();
    this.buffer.clear();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close(1000, "page closed");
      } catch {
        // already closed
      }
    }
    this.options.sink.status("closed");
  }

  /** Try now instead of waiting for the next attempt (the browser says it is online again). */
  reconnectNow(): void {
    if (this.stopped || this.socket) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.connect();
  }

  /** Tell the server where the person is. Called on a timer and when the screen changes. */
  sendHeartbeat(): void {
    const location = this.options.getLocation?.() ?? { screen: "unknown" };
    this.send({ type: "heartbeat", screen: location.screen, ...(location.item_id ? { item_id: location.item_id } : {}), ...(location.session_id ? { session_id: location.session_id } : {}) });
  }

  /** The edit form is in use: the lock on this element stays. */
  sendStillEditing(elementId: Id): void {
    this.send({ type: "still_editing", element_id: elementId });
  }

  get isOpen(): boolean {
    return this.socket?.readyState === OPEN;
  }

  /** The number of the last notice applied (null before the server said hello). */
  get head(): number | null {
    return this.applied;
  }

  // ------------------------------------------------------------------ connection

  private connect(): void {
    if (this.stopped) return;
    const create = this.options.createSocket ?? ((url: string) => new WebSocket(url) as unknown as SocketLike);
    this.connectStartedAt = (this.options.now ?? Date.now)();
    this.options.sink.status(this.joinedOnce ? "reconnecting" : "connecting");
    let socket: SocketLike;
    try {
      socket = create(this.options.url ?? buildWsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      if (this.socket === socket) this.options.sink.status("open");
    };
    socket.onmessage = (event) => {
      if (this.socket === socket) this.handleData(event.data);
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.handleClose(event.code);
    };
    socket.onerror = () => {
      // A close follows; nothing to do here.
    };
  }

  private handleClose(code: number): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    if (this.stopped) return;
    const sink = this.options.sink;
    if (code === WS_CLOSE_NOT_SIGNED_IN) {
      sink.status("closed");
      this.stopped = true;
      this.clearTimers();
      sink.authLost("not_signed_in");
      return;
    }
    if (code === WS_CLOSE_ACCOUNT_DISABLED) {
      sink.status("closed");
      this.stopped = true;
      this.clearTimers();
      sink.authLost("account_disabled");
      return;
    }
    if (code === WS_CLOSE_MEMBERSHIP_CHANGED) {
      sink.membershipChanged();
      this.scheduleReconnect();
      return;
    }
    if (code === WS_CLOSE_RESYNC) {
      this.needsResync = true;
      this.scheduleReconnect(true);
      return;
    }
    this.failedInARow += 1;
    const limit = this.options.checkSessionAfter ?? 3;
    if (this.failedInARow >= limit) {
      this.failedInARow = 0;
      sink.checkSession();
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(immediate = false): void {
    if (this.stopped) return;
    this.options.sink.status("reconnecting");
    const base = this.options.backoffBaseMs ?? 1000;
    const max = this.options.backoffMaxMs ?? 30_000;
    const random = this.options.random ?? Math.random;
    const delay = immediate ? 0 : Math.round(Math.min(max, base * 2 ** this.attempt) * (0.5 + random() * 0.5));
    this.attempt += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearTimers(): void {
    for (const timer of [this.reconnectTimer, this.retryTimer, this.headTimer, this.invalidateTimer]) {
      if (timer) clearTimeout(timer);
    }
    this.reconnectTimer = this.retryTimer = this.headTimer = this.invalidateTimer = null;
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private send(message: Record<string, unknown>): void {
    if (this.socket?.readyState !== OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  // ------------------------------------------------------------------ messages

  private handleData(data: unknown): void {
    const message = parseServerMessage(data);
    if (!message) return; // a message that is not in the plan's shape is ignored
    const sink = this.options.sink;
    switch (message.type) {
      case "hello":
        this.handleHello(message);
        break;
      case "event":
        this.handleNotice(message.notice);
        break;
      case "head":
        this.handleHead(message.number);
        break;
      case "resync":
        // The socket is about to close with 4409. Everything is fetched again after the next hello.
        this.needsResync = true;
        break;
      case "presence":
        sink.presence(message.entries);
        break;
      case "lock":
        sink.lock(message);
        break;
      case "files":
        sink.files(message.status);
        break;
      case "counts":
        sink.counts(message);
        break;
      case "agent.turn_started":
      case "agent.delta":
      case "agent.block":
      case "agent.tool":
      case "agent.turn_ended":
        sink.agent(message);
        break;
      case "notice":
        sink.personalNotice(message);
        this.queue(NOTICE_INVALIDATIONS[message.kind] ?? []);
        break;
      case "point_to":
        sink.pointTo(message);
        break;
      case "error":
        sink.error(message);
        break;
    }
  }

  private handleHello(hello: WsHello): void {
    const sink = this.options.sink;
    this.attempt = 0;
    this.failedInARow = 0;
    sink.status("open");
    sink.hello(hello);
    sink.serverHead(hello.head);
    this.startHeartbeat(hello.heartbeat_s);
    this.sendHeartbeat();

    const first = !this.joinedOnce;
    this.joinedOnce = true;
    if (first) sink.firstJoin(this.connectStartedAt);

    if (this.applied === null) {
      this.applied = hello.head;
      sink.applied(hello.head);
    } else if (this.needsResync || hello.head < this.applied) {
      // The server forgot some of what this client has seen (a resync, or a restored workspace).
      this.needsResync = false;
      this.buffer.clear();
      this.applied = hello.head;
      sink.applied(hello.head);
      sink.refetchAll();
    } else if (hello.head > this.applied) {
      this.requestCatchUp();
    }
  }

  private startHeartbeat(seconds: number): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    const ms = Math.max(5, seconds || 30) * 1000;
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), ms);
  }

  private handleHead(number: number): void {
    this.options.sink.serverHead(number);
    if (this.applied === null || number <= this.applied) return;
    // The last notice may still be on its way; give it a moment before asking.
    if (this.headTimer) clearTimeout(this.headTimer);
    this.headTimer = setTimeout(() => {
      this.headTimer = null;
      if (this.applied !== null && number > this.applied) this.requestCatchUp();
    }, this.options.headSettleMs ?? 500);
  }

  private handleNotice(notice: EventNotice): void {
    if (this.applied === null) this.applied = notice.number - 1;
    if (notice.number <= this.applied) return; // already applied
    if (this.catchingUp) {
      // The answer of the catch-up and the kept notices are applied together when it comes.
      this.buffer.set(notice.number, notice);
      return;
    }
    if (notice.number === this.applied + 1) {
      this.applyNotice(notice);
      this.drain();
      return;
    }
    this.buffer.set(notice.number, notice);
    this.requestCatchUp();
  }

  private applyNotice(notice: EventNotice): void {
    const mine = this.options.membershipId;
    const forSomeoneElse = notice.private_to && mine && notice.private_to !== mine;
    if (!forSomeoneElse) {
      this.options.sink.notice(notice);
      this.queue(invalidationsFor(notice.type));
    }
    this.applied = notice.number;
    this.options.sink.applied(notice.number);
  }

  /** Apply the kept notices that now follow the last applied one. */
  private drain(): void {
    while (this.applied !== null) {
      const next = this.buffer.get(this.applied + 1);
      if (!next) break;
      this.buffer.delete(next.number);
      this.applyNotice(next);
    }
    if (this.buffer.size > 0 && !this.catchingUp) this.requestCatchUp();
  }

  // ------------------------------------------------------------------ catching up

  private requestCatchUp(): void {
    if (this.stopped) return;
    if (this.catchingUp) {
      this.catchUpAgain = true;
      return;
    }
    void this.catchUp();
  }

  private async catchUp(): Promise<void> {
    this.catchingUp = true;
    const sink = this.options.sink;
    try {
      do {
        this.catchUpAgain = false;
        let cursor = this.applied ?? 0;
        let head = cursor;
        let resync = false;
        const found: EventNotice[] = [];
        for (let page = 0; page < CATCH_UP_MAX_PAGES; page += 1) {
          const answer = await this.options.fetchEventsAfter(cursor, CATCH_UP_PAGE);
          if (this.stopped) return;
          head = Math.max(head, answer.head);
          if (answer.resync_required) {
            resync = true;
            break;
          }
          found.push(...answer.events);
          const last = answer.events[answer.events.length - 1];
          if (!last || answer.events.length < CATCH_UP_PAGE || last.number >= answer.head) break;
          cursor = last.number;
        }
        if (resync) {
          this.buffer.clear();
          this.applied = head;
          sink.applied(head);
          sink.refetchAll();
          continue;
        }
        const merged = new Map<number, EventNotice>();
        for (const notice of found) merged.set(notice.number, notice);
        for (const notice of this.buffer.values()) merged.set(notice.number, notice);
        this.buffer.clear();
        for (const notice of [...merged.values()].sort((a, b) => a.number - b.number)) {
          if (this.applied === null || notice.number > this.applied) this.applyNotice(notice);
        }
        this.applied = Math.max(this.applied ?? 0, head);
        sink.applied(this.applied);
        sink.serverHead(this.applied);
      } while (this.catchUpAgain && !this.stopped);
    } catch (error) {
      if (isRefusal(error, "not_signed_in")) {
        sink.authLost("not_signed_in");
      } else if (!this.stopped) {
        // Try again soon; the socket's own reconnect covers a longer outage.
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          this.requestCatchUp();
        }, CATCH_UP_RETRY_MS);
      }
    } finally {
      this.catchingUp = false;
    }
  }

  // ------------------------------------------------------------------ invalidation

  private queue(names: readonly QueryName[]): void {
    if (names.length === 0) return;
    for (const name of names) this.pending.add(name);
    if (this.invalidateTimer) return;
    this.invalidateTimer = setTimeout(() => this.flush(), this.options.coalesceMs ?? 150);
  }

  /** Send the gathered invalidations now. */
  flush(): void {
    if (this.invalidateTimer) clearTimeout(this.invalidateTimer);
    this.invalidateTimer = null;
    if (this.pending.size === 0) return;
    const names = [...this.pending];
    this.pending.clear();
    this.options.sink.invalidate(names);
  }
}
